/* eslint-disable import/no-cycle */
import * as React from 'react';
import { isLegacyEdge } from '../../utils/BrowserDetection';
import { makeTestId } from '../../utils/Declarative';
import { GlobalPointerEventCapturer } from '../../utils/GlobalPointerEventCapturer';
import { getClientPosition, isTouchEvent } from '../../utils/UserInteraction';
import { HandleType } from './SliderBaseWidget';
import * as SliderHandleContainerCoordinateLogic from './SliderHandleContainerCoordinateLogic';
import * as SliderHandleContainerDragHandleLogic from './SliderHandleContainerDragHandleLogic';
import { DragHandle } from './SliderHandleContainerDragHandleLogic';
import { SliderHandleIconType, SliderHandleWidget } from './SliderHandleWidget';
import { getSliderHandleContainerStyles } from './SliderStyles';
import { SliderValueLogic } from './SliderValueLogic';

export interface SliderHandleContainerProps {
  /**
   * If true, dragging is not allowed, key down events are ignored, and the handles are displayed in a 'disabled' state.
   */
  disabled?: boolean;

  /**
   * This prop controls the number of handles and their positions.  If both left and right are defined, then right
   * must be greater than or equal to left.
   *
   * The positions are a proportion of the horizontal space.  They should be numbers in the range [0.0, 1.0].
   */
  handles: { leftHandlePosition: number, rightHandlePosition?: number };

  /**
   * This prop contains the aria attributes for each handle.
   */
  handlesAriaAttributes: { leftHandleAriaAttributes: React.AriaAttributes, rightHandleAriaAttributes?: React.AriaAttributes };

  /**
   * Called when a drag starts for a single handle.
   *
   * The position is the point where the drag is starting, which is a proportion (a number in the range [0.0,1.0]) of
   * the horizontal space inside the container.
   */
  onHandleDragStart: (handleType: HandleType, position: number) => void;

  /**
   * Called when a drag on a single handle moves the handle.
   *
   * The position is the point where the drag has moved to, which is a proportion (a number in the range [0.0,1.0]) of
   * the horizontal space inside the container.
   */
  onHandleDragMove: (handleType: HandleType, position: number) => void;

  /**
   * Called when a keyDown event occurs that is related to one of the handles and is relevant.
   */
  onHandleKeyDown: (handle: HandleType, key: string) => void;

  /**
   * Called when an active handle interaction completes, i.e. pointerUp or keyUp.
   */
  onHandleRelease: () => void;

  /**
   * Called when a drag on both handles starts.
   */
  onBothHandleDragStart: () => void;

  /**
   * Called when a drag on both handles moves the handles.
   *
   * The positions are the point where the drag has moved the handles to, which is a proportion (a number in the range
   * [0.0,1.0]) of the horizontal space inside the container.
   */
  onBothHandleDragMove: (leftPosition: number, rightPosition: number) => void;

  /** Called when the range handle's focus changes. */
  onRangeHandleFocus: (focus: boolean) => void;

  /**
   * Customize the CSS styles of the container element.
   */
  style?: React.CSSProperties;

  /**
   * Test id to be attached to the slider and its contents.
   * The suffix '-SliderHandleContainer' will be automatically added.
   */
  testId: string;
}

export interface SliderHandleContainerState {
  /* During a drag this is set and is used when both handles are being dragged. */
  dragOriginInClientCoordinates?: { x: number, y: number };

  /* During a drag, this is the handle being dragged.  When there is not an ongoing drag, this is undefined. */
  handleBeingDragged?: HandleType;

  /**
   * During a drag that is initiated by a touch event,
   * this is the handle being dragged.
   * When there is not an ongoing drag, this is undefined.
   */
  handleBeingTouched?: HandleType;
}

/**
 * This component is responsible for displaying the handles and making them interactive.  I.e. dragging.
 *
 * It does not deal with the min and maxes or the actual value of the slider.  That is all handled in the
 * SliderWidget itself.
 *
 * Note that to deal with mouse/touch events outside the bounds of the element and window, we have to attach event
 * handlers to the window itself.
 */
export class SliderHandleContainer extends React.Component<SliderHandleContainerProps, SliderHandleContainerState> {
  private globalPointerEventCapturer: GlobalPointerEventCapturer = undefined!; // this gets set on mount
  private containerElement: HTMLElement | null = null;
  // We need this ref to get bounding client rects and to focus() it.
  private leftHandleWidgetRef: SliderHandleWidget | null = null;
  // We need this ref to get bounding client rects and to focus() it.
  private rightHandleWidgetRef: SliderHandleWidget | null = null;
  // We need this ref to focus the range slider.
  private rangeHandleElement: HTMLElement | null = null;
  /** During a drag this is set and is used when both handles are being dragged. */
  public handlePositionsAtDragStart?: { leftHandlePosition: number, rightHandlePosition?: number };

  public constructor(props: SliderHandleContainerProps) {
    super(props);
    this.state = {};
  }

  private getHandleCount = (): 1 | 2 => {
    return typeof this.props.handles.rightHandlePosition === 'undefined' ? 1 : 2;
  }

  private isSingleHandleBeingDragged = (handleType?: HandleType): handleType is HandleType.Left | HandleType.Right => {
    return handleType === HandleType.Left || handleType === HandleType.Right;
  }

  private determineSingleHandleDragOffsetIfNeeded = (
    handleType: HandleType,
    isDragDirectlyOnHandle: boolean,
    eventPositionAsProportion: number
  ): number | undefined => {
    if (!isDragDirectlyOnHandle) {
      return undefined;
    }

    let currentHandlePosition;
    if (handleType === HandleType.Left) {
      currentHandlePosition = this.props.handles.leftHandlePosition;
    } else if (handleType === HandleType.Right) {
      currentHandlePosition = this.props.handles.rightHandlePosition;
    }

    return currentHandlePosition ? currentHandlePosition - eventPositionAsProportion : undefined;
  }

  private determineWhichHandleShouldBeDraggedForEvent = (
    touchEvent: boolean,
    eventClientCoordinates: { x: number, y: number }
  ): DragHandle | undefined => {

    if (!touchEvent) {
      if (this.getHandleCount() === 1) {
        return SliderHandleContainerDragHandleLogic.determineWhichHandleShouldBeDraggedForMouseAndOneHandle(
          eventClientCoordinates,
          this.leftHandleWidgetRef!.getBoundingClientRectForIcon()
        );
      } else {
        return SliderHandleContainerDragHandleLogic.determineWhichHandleShouldBeDraggedForMouseAndTwoHandles(
          eventClientCoordinates,
          this.leftHandleWidgetRef!.getBoundingClientRectForIcon(),
          this.rightHandleWidgetRef!.getBoundingClientRectForIcon()
        );
      }
    } else {
      if (this.getHandleCount() === 1) {
        return SliderHandleContainerDragHandleLogic.determineWhichHandleShouldBeDraggedForTouchAndOneHandle(
          eventClientCoordinates,
          this.leftHandleWidgetRef!.getBoundingClientRectForInteractiveArea()
        );
      } else {
        return SliderHandleContainerDragHandleLogic.determineWhichHandleShouldBeDraggedForTouchAndTwoHandles(
          eventClientCoordinates,
          this.leftHandleWidgetRef!.getBoundingClientRectForInteractiveArea(),
          this.rightHandleWidgetRef!.getBoundingClientRectForInteractiveArea(),
          this.rangeHandleElement!.getBoundingClientRect()
        );
      }
    }
  }

  private fireHandleDragStartEvents = (
    handleType: HandleType,
    eventPositionAsProportion: number,
    singleHandleDragOffsetAsProportion?: number
  ): void => {
    if (this.isSingleHandleBeingDragged(handleType)) {
      if (this.props.onHandleDragStart && !isNaN(eventPositionAsProportion)) {
        const position = eventPositionAsProportion + (singleHandleDragOffsetAsProportion || 0);
        this.props.onHandleDragStart(handleType, position);
      }
    } else if (this.props.onBothHandleDragStart) {
      this.props.onBothHandleDragStart();
    }
  }

  private fireHandleDragMoveEvents = (leftHandlePosition: number, rightHandlePosition?: number): void => {
    if (this.isSingleHandleBeingDragged(this.state.handleBeingDragged)) {
      if (this.props.onHandleDragMove && !isNaN(leftHandlePosition)) {
        this.props.onHandleDragMove(
          this.state.handleBeingDragged,
          leftHandlePosition
        );
      }
    } else if (typeof rightHandlePosition !== 'undefined' && !isNaN(leftHandlePosition) && !isNaN(rightHandlePosition)) {
      this.props.onBothHandleDragMove(leftHandlePosition, rightHandlePosition);
    }
  }

  private handleHandleRelease = (): void => {
    this.setState({ handleBeingDragged: undefined });
    if (this.props.onHandleRelease) {
      this.props.onHandleRelease();
    }
  }

  private setKeyboardFocusOnHandle = (handleType: HandleType): void => {
    let handleElementRefToFocus: HTMLElement | SliderHandleWidget | null = null;

    if (handleType === HandleType.Left) {
      handleElementRefToFocus = this.leftHandleWidgetRef;
    } else if (handleType === HandleType.Right) {
      handleElementRefToFocus = this.rightHandleWidgetRef;
    } else if (handleType === HandleType.Both) {
      handleElementRefToFocus = this.rangeHandleElement;
    }

    handleElementRefToFocus?.focus();
  }

  private dragStart = (
    handleType: HandleType,
    eventClientCoordinates: { x: number, y: number },
    isDirectlyOnHandle: boolean,
    startedByTouchEvent: boolean
  ): void => {
    if (!this.containerElement) {
      return;
    }

    const positionAsProportion = SliderHandleContainerCoordinateLogic.convertClientCoordinateIntoProportionOfContainer(
      eventClientCoordinates.x, this.containerElement.getBoundingClientRect()
    );

    this.globalPointerEventCapturer.addListeners();

    // We set this offset if the user interacted directly w/ the handle, and use it later to avoid jerking the handle
    // directly to the location of the event.
    const singleHandleDragOffsetAsProportion = this.determineSingleHandleDragOffsetIfNeeded(
      handleType, isDirectlyOnHandle, positionAsProportion
    );

    this.handlePositionsAtDragStart = {
      leftHandlePosition: (handleType === HandleType.Left) ? positionAsProportion : this.props.handles.leftHandlePosition,
      rightHandlePosition: (handleType === HandleType.Right) ? positionAsProportion : this.props.handles.rightHandlePosition
    };

    this.setState({
      handleBeingTouched: startedByTouchEvent ? handleType : undefined,
      handleBeingDragged: handleType,
      dragOriginInClientCoordinates: eventClientCoordinates,
    });

    this.fireHandleDragStartEvents(handleType, positionAsProportion, singleHandleDragOffsetAsProportion);

    // The reason we are doing this is that, in the case of mouse events, the initial down event may not have been on
    // the handle.  It may have been on the track.  So we have to manually give input focus to the handle.
    this.setKeyboardFocusOnHandle(handleType);
  }

  private dragMove = (eventClientCoordinates: { x: number, y: number }): void => {
    if (!this.containerElement) {
      return;
    }
    const isSingleHandleBeingDragged = this.isSingleHandleBeingDragged(this.state.handleBeingDragged);

    if (isSingleHandleBeingDragged && this.props.onHandleDragMove) {
      const handlePositionAtDragStart =
        this.state.handleBeingDragged === HandleType.Left ?
          this.handlePositionsAtDragStart!.leftHandlePosition :
          this.handlePositionsAtDragStart!.rightHandlePosition!;
      const newPosition = SliderHandleContainerCoordinateLogic.calculateProportionalPositionWhenDraggingSingleHandle(
        this.state.dragOriginInClientCoordinates!.x,
        handlePositionAtDragStart,
        eventClientCoordinates.x,
        this.containerElement.getBoundingClientRect()
      );
      this.fireHandleDragMoveEvents(newPosition);
    } else if (!isSingleHandleBeingDragged && this.props.onBothHandleDragMove) {
      // Both handles are being dragged
      const newPositions = SliderHandleContainerCoordinateLogic.calculateProportionalPositionsWhenDraggingBothHandles(
        this.state.dragOriginInClientCoordinates!.x,
        this.handlePositionsAtDragStart!.leftHandlePosition,
        this.handlePositionsAtDragStart!.rightHandlePosition!,
        eventClientCoordinates.x,
        this.containerElement.getBoundingClientRect()
      );
      this.fireHandleDragMoveEvents(newPositions.leftHandlePosition, newPositions.rightHandlePosition);
    }
  }

  private handleContainerRef = (element: HTMLElement | null): void => {
    this.containerElement = element;
    if (this.containerElement) {
      this.globalPointerEventCapturer = new GlobalPointerEventCapturer(this.handleGlobalPointerMove, this.handleGlobalPointerUp);
    }
  }

  private setLeftHandleWidgetRef = (component: SliderHandleWidget): void => {
    this.leftHandleWidgetRef = component;
  }

  private setRightHandleWidgetRef = (component: SliderHandleWidget): void => {
    this.rightHandleWidgetRef = component;
  }

  private setRangeHandleRef = (element: HTMLElement): void => {
    this.rangeHandleElement = element;
  }

  private handlePointerDown = (event: React.SyntheticEvent<HTMLElement>): void => {
    if (this.props.disabled || !this.leftHandleWidgetRef || (this.getHandleCount() === 2 && !this.rightHandleWidgetRef)) {
      return;
    }

    const castedEvent = event as React.TouchEvent<HTMLElement> | React.MouseEvent<HTMLElement>;
    const clientCoordinates = getClientPosition(castedEvent);
    const dragInfo = this.determineWhichHandleShouldBeDraggedForEvent(isTouchEvent(castedEvent), clientCoordinates);

    if (!dragInfo) {
      return;
    }

    event.preventDefault(); // this will prevent touch events from also firing the corresponding mouse event
    event.stopPropagation();

    this.dragStart(dragInfo.handleType, clientCoordinates, dragInfo.directlyOnIcon, isTouchEvent(castedEvent));
  }

  private handleGlobalPointerMove = (event: MouseEvent | TouchEvent): void => {
    if (this.props.disabled || !this.containerElement) {
      return;
    }

    event.preventDefault(); // this will prevent touch events from also firing the corresponding mouse event
    event.stopPropagation();

    this.dragMove(getClientPosition(event));
  }

  private handleGlobalPointerUp = (event: MouseEvent | TouchEvent): void => {
    this.globalPointerEventCapturer.removeListeners();
    this.handlePositionsAtDragStart = undefined;
    this.setState({
      handleBeingTouched: undefined,
      dragOriginInClientCoordinates: undefined,
    });

    this.handleHandleRelease();

    if (this.props.disabled) {
      return;
    }

    event.preventDefault(); // this will prevent touch events from also firing the corresponding mouse event
    event.stopPropagation();
  }

  private makeKeyDownHandler = (handleType: HandleType): ((event: React.KeyboardEvent<HTMLElement>) => void) => {
    return (event: React.KeyboardEvent<HTMLElement>): void => {
      if (this.props.disabled || !this.props.onHandleKeyDown) { return; }
      if (SliderValueLogic.KeysRelevantForManipulatingSlider[event.key]) {
        this.setState({ handleBeingDragged: handleType });
        this.props.onHandleKeyDown(handleType, event.key);
        event.preventDefault();
        event.stopPropagation();
      }
    };
  }

  private handleHandleKeyUp = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (this.props.disabled) { return; }
    if (SliderValueLogic.KeysRelevantForManipulatingSlider[event.key]) {
      this.handleHandleRelease();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private handleBlur = (e: React.FocusEvent<HTMLElement>): void => {
    if (!e.currentTarget.contains(e.relatedTarget as HTMLElement) && !!this.state.handleBeingDragged) {
      this.handleHandleRelease();
    }
  }

  public componentWillUnmount(): void {
    this.globalPointerEventCapturer.removeListeners();
  }

  private renderLeftHandle = (): React.ReactNode => {
    if (isNaN(this.props.handles.leftHandlePosition)) {
      return null;
    }
    const isActive = this.state.handleBeingDragged === HandleType.Left || this.state.handleBeingDragged === HandleType.Both;
    const isBeingTouched: boolean = this.state.handleBeingTouched === HandleType.Left || this.state.handleBeingTouched === HandleType.Both;
    const iconType = this.getHandleCount() === 1 ? SliderHandleIconType.Normal : SliderHandleIconType.Left;
    return (
      <SliderHandleWidget
        ariaAttributes={this.props.handlesAriaAttributes.leftHandleAriaAttributes}
        disabled={this.props.disabled}
        iconType={iconType}
        isActive={isActive}
        isBeingTouched={isBeingTouched}
        onKeyDown={this.makeKeyDownHandler(HandleType.Left)}
        onKeyUp={this.handleHandleKeyUp}
        position={this.props.handles.leftHandlePosition}
        ref={this.setLeftHandleWidgetRef}
        testId={this.props.testId}
      />
    );
  }

  private renderRightHandle = (): React.ReactNode => {
    // eslint-disable-next-line max-len
    if (this.getHandleCount() === 1 || typeof this.props.handles.rightHandlePosition === 'undefined' || isNaN(this.props.handles.rightHandlePosition)) {
      return null;
    }
    const isActive = this.state.handleBeingDragged === HandleType.Right || this.state.handleBeingDragged === HandleType.Both;
    const isBeingTouched: boolean = this.state.handleBeingTouched === HandleType.Right || this.state.handleBeingTouched === HandleType.Both;
    return (
      <SliderHandleWidget
        ariaAttributes={this.props.handlesAriaAttributes.rightHandleAriaAttributes!}
        disabled={this.props.disabled}
        iconType={SliderHandleIconType.Right}
        isActive={isActive}
        isBeingTouched={isBeingTouched}
        onKeyDown={this.makeKeyDownHandler(HandleType.Right)}
        onKeyUp={this.handleHandleKeyUp}
        position={this.props.handles.rightHandlePosition}
        ref={this.setRightHandleWidgetRef}
        testId={this.props.testId}
      />
    );
  }

  private handleRangeHandleFocusBlur = (e: React.FocusEvent<HTMLElement>): void => {
    if (this.props.onRangeHandleFocus) {
      this.props.onRangeHandleFocus(e.type === 'focus');
    }
  }

  private renderRangeHandle(style: React.CSSProperties): React.ReactNode {
    if (this.props.disabled || isNaN(this.props.handles.leftHandlePosition) || this.getHandleCount() === 1 ||
      // eslint-disable-next-line eqeqeq
      this.props.handles.rightHandlePosition == /* <-- intentional */ undefined || isNaN(this.props.handles.rightHandlePosition)
    ) {
      return null;
    }

    const leftPositionAsPercentage = this.props.handles.leftHandlePosition * 100;
    const rightPositionAsPercentage = 100 - (this.props.handles.rightHandlePosition * 100);

    return (
      <span
        data-tb-test-id={makeTestId(this.props.testId, 'RangeHandle')}
        onBlur={this.handleRangeHandleFocusBlur}
        onFocus={this.handleRangeHandleFocusBlur}
        onKeyDown={this.makeKeyDownHandler(HandleType.Both)}
        onKeyUp={this.handleHandleKeyUp}
        ref={this.setRangeHandleRef}
        style={{ ...style, left: `${leftPositionAsPercentage}%`, right: `${rightPositionAsPercentage}%` }}
        // FIXME TFSID 1486781
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      />
    );
  }

  public render(): React.ReactNode {
    const dataTestId = makeTestId(this.props.testId, 'SliderHandleContainer');
    const styles = getSliderHandleContainerStyles();

    const containerStyle = styles.Container;
    if (isLegacyEdge) {
      containerStyle.touchAction = 'none';
    }

    // The container.  This will be the size of the whole slider (sans some padding).
    // The additional onTouchStart handler here is needed for mobile browsers.
    return (
      <div
        data-tb-test-id={dataTestId}
        ref={this.handleContainerRef}
        onBlur={this.handleBlur}
        onMouseDown={this.handlePointerDown}
        onTouchStart={this.handlePointerDown}
        style={containerStyle}
      >
        {this.renderLeftHandle()}
        {this.renderRangeHandle(styles.RangeHandleArea)}
        {this.renderRightHandle()}
      </div>
    );
  }
}
